import * as React from 'react';
import { hasDashboardAccess } from 'services/client/token';
import { AuthStatus } from 'constants/auth';
import { FINRA_EXCHANGE_ID } from 'constants/darkpool';
import {
  useGetFullFeedTickerEquityPrintFlowLazyQuery,
  useGetTickerDarkPoolHistoricalBaselineLazyQuery,
  useGetDarkPoolCountFullLazyQuery,
  useGetLitPoolCountFullLazyQuery,
  useGetAllPoolCountFullLazyQuery,
  useHasNewFullFeedTickerEquityPrintFlowQuery,
} from 'types/generated/client';
import {
  USE_TIMESTAMP_POLLING,
  getInitialPollingTimestamp,
  REALTIME_DARKPOOL_ENABLED,
} from 'constants/flags';
import { REALTIME_URL } from 'constants/api';
import { useTickerDarkPoolRealtime } from './useTickerDarkPoolRealtime';
import { mergeLiveHistory } from './mergeLiveHistory';
import DarkPoolTable from 'components/DarkPoolTable/DarkPoolTable';
import { DarkPoolTrade } from 'components/DarkPoolTable/props';
import getSignaturePrintStatus from 'utils/shared/trades/getSignaturePrintStatus';
import { getIsGoldenDarkPool } from 'utils/shared/trades/getIsGoldenDarkPool';
import { SignaturePrintStatus } from 'utils/shared/trades/types';
import { getTickerFlowSinceDate } from 'utils/shared/trades/tickerFlowWindow';
import { getDarkPoolFilterFunction } from 'hooks/useCommandCenter';
import DarkPoolOverviewTable from './DarkPoolOverviewTable';
import { useApiGateway } from 'hooks/useApi';
import { GetDarkPoolDataResponse } from 'constants/payloads/darkPool';
import { TimeFilters as UtilTimeFilters } from 'utils/shared/darkpool/calculateDarkPoolSummary';
import {
  TickerDarkPoolTableProps,
  DarkPoolAggregatedDetails,
  TIME_FILTERS,
  TimeFilters,
  FeedType,
  filterTradesForDateSelection,
  getDateRangeForDateFilter,
} from './props';

import { DarkFeedTypeToggle, AutoTopGrid } from './styles';
// import demoDarkpoolData from './demoDarkpoolData';

const POLL_INTERVAL = 60_000;

const TickerDarkPoolTable: React.FC<React.PropsWithChildren<TickerDarkPoolTableProps>> = ({
  activeTicker,
  viewer,
  activeTimeFilter,
  injectedPageData,
  hideHeader,
  hideLevelsColumn,
  tickerDetailsMap,
  filterState,
  dateRange,
  isLatestPreset = true,
  useLatestData,
  dateFilterValue,
}) => {
  const [feedType, setFeedType] = React.useState(FeedType.Dark);
  const hasAccess = viewer.status === AuthStatus.User && hasDashboardAccess(viewer);
  const shouldUseLatestData = useLatestData ?? isLatestPreset;

  // Initialize with 24 hours ago to ensure initial data load
  const [lastSeenTimestamp, setLastSeenTimestamp] = React.useState<string>(
    getInitialPollingTimestamp,
  );

  // === MINIMAL ADDITION: API summary for users without access ===
  const {
    get: fetchDarkPoolSummary,
    data: apiSummary,
    // isLoading: apiLoading,
  } = useApiGateway<null, GetDarkPoolDataResponse>();

  // PERF: literal rolling date bound so full_feed_equity_print_flow uses the date index instead
  // of scanning the ticker's whole history (30s+ / edge 504). See utils/shared/trades/tickerFlowWindow.
  const sinceDate = React.useMemo(() => getTickerFlowSinceDate(), []);

  const [
    fullFeedQuery,
    { data: fullData, error: fullError, loading: fullLoading, refetch: fullRefetch },
  ] = useGetFullFeedTickerEquityPrintFlowLazyQuery({
    variables: {
      ticker: activeTicker.toUpperCase(),
      sinceDate,
    },
    fetchPolicy: 'no-cache',
  });
  const [
    fullHistoricalQuery,
    {
      data: fullHistoricalData,
      error: fullHistoricalError,
      loading: fullHistoricalLoading,
      refetch: fullHistoricalRefetch,
    },
  ] = useGetTickerDarkPoolHistoricalBaselineLazyQuery({ fetchPolicy: 'no-cache' });

  // New count queries for Dark/Lit/All using equity_print_flow_full
  const [darkPoolCountQuery, { data: darkCountData, refetch: darkCountRefetch }] =
    useGetDarkPoolCountFullLazyQuery({
      variables: {
        ticker: activeTicker.toUpperCase(),
      },
    });
  const [litPoolCountQuery, { data: litCountData, refetch: litCountRefetch }] =
    useGetLitPoolCountFullLazyQuery({
      variables: {
        ticker: activeTicker.toUpperCase(),
      },
    });
  const [allPoolCountQuery, { data: allCountData, refetch: allCountRefetch }] =
    useGetAllPoolCountFullLazyQuery({
      variables: {
        ticker: activeTicker.toUpperCase(),
      },
    });

  const isDarkFeed = feedType === FeedType.Dark;
  const isLitFeed = feedType === FeedType.Lit;

  // Timestamp-based polling (feature flagged)
  // All feed types share the same full_feed table, so one polling query suffices
  const {
    data: latestData,
    startPolling,
    stopPolling,
  } = useHasNewFullFeedTickerEquityPrintFlowQuery({
    variables: { ticker: activeTicker?.toUpperCase() || '', since: lastSeenTimestamp },
    skip: !USE_TIMESTAMP_POLLING || !activeTicker || !hasAccess || !shouldUseLatestData,
  });

  // Define feed type buttons dynamically
  const feedTypeButtons = [
    { type: FeedType.Dark, label: 'Dark' },
    { type: FeedType.Lit, label: 'Lit' },
    { type: FeedType.All, label: 'All' },
  ];

  const getCount = isDarkFeed
    ? darkPoolCountQuery
    : isLitFeed
    ? litPoolCountQuery
    : allPoolCountQuery;

  const refetchCount = isDarkFeed
    ? darkCountRefetch
    : isLitFeed
    ? litCountRefetch
    : allCountRefetch;

  const count = isDarkFeed
    ? darkCountData?.equity_print_flow_full_aggregate.aggregate?.count
    : isLitFeed
    ? litCountData?.equity_print_flow_full_aggregate.aggregate?.count
    : allCountData?.equity_print_flow_full_aggregate.aggregate?.count;

  const error = shouldUseLatestData ? fullError : fullHistoricalError;
  const loading = shouldUseLatestData ? fullLoading : fullHistoricalLoading;
  const injectedRawTrades: Array<DarkPoolTrade> = injectedPageData?.equity_print_flow_full || [];

  // Apply feed type filter to injected data (same as live data filtering)
  const injectedTrades = React.useMemo(() => {
    if (isDarkFeed) {
      return injectedRawTrades.filter((t) => t.exchangeId === FINRA_EXCHANGE_ID);
    } else if (isLitFeed) {
      return injectedRawTrades.filter((t) => t.exchangeId !== FINRA_EXCHANGE_ID);
    }
    return injectedRawTrades;
  }, [injectedRawTrades, isDarkFeed, isLitFeed]);

  const latestTrades: Array<DarkPoolTrade> =
    fullData?.full_feed_equity_print_flow &&
    fullData.full_feed_equity_print_flow.length > 0 &&
    fullData.full_feed_equity_print_flow[0].ticker === activeTicker.toUpperCase()
      ? fullData.full_feed_equity_print_flow
      : [];
  const historicalTrades: Array<DarkPoolTrade> =
    fullHistoricalData?.equity_print_feed_baseline &&
    fullHistoricalData.equity_print_feed_baseline.length > 0 &&
    fullHistoricalData.equity_print_feed_baseline[0].ticker === activeTicker.toUpperCase()
      ? fullHistoricalData.equity_print_feed_baseline
      : [];
  // Realtime (Phase 4): the DO owns today for this ticker; the loaded latest feed
  // owns pre-today. Only in the "latest" preset, paid, non-injected. Dark pool is
  // gated by REALTIME_DARKPOOL_ENABLED (the same feed as the main dark-pool table).
  const realtimeDpEnabled =
    REALTIME_DARKPOOL_ENABLED &&
    !!REALTIME_URL &&
    shouldUseLatestData &&
    hasAccess &&
    !injectedPageData &&
    !!activeTicker;
  const {
    rows: realtimeDpRows,
    sessionDate: realtimeDpSessionDate,
    status: realtimeDpStatus,
  } = useTickerDarkPoolRealtime({ ticker: activeTicker, enabled: realtimeDpEnabled });
  const usingRealtimeDp =
    realtimeDpEnabled &&
    (realtimeDpStatus === 'live' ||
      (realtimeDpStatus === 'connecting' && realtimeDpRows.length > 0));

  // Memoized so the realtime path's fresh merge/filter arrays don't bust every
  // downstream memo (filteredByFeedType, sorting) on each unrelated render (§17.10.3).
  const baseTrades = React.useMemo(
    () =>
      usingRealtimeDp
        ? mergeLiveHistory(
            // Filter to the active ticker in the render path — the hook clears its bucket in
            // a post-render effect, so a ticker switch would briefly show the old ticker's
            // live prints under the new header (§17.9 X5).
            realtimeDpRows.filter(
              (r) => typeof r.ticker === 'string' && r.ticker === activeTicker?.toUpperCase(),
            ) as unknown as DarkPoolTrade[],
            latestTrades,
            realtimeDpSessionDate,
          )
        : shouldUseLatestData
        ? latestTrades
        : historicalTrades,
    [
      usingRealtimeDp,
      realtimeDpRows,
      activeTicker,
      latestTrades,
      realtimeDpSessionDate,
      shouldUseLatestData,
      historicalTrades,
    ],
  );

  // Apply client-side filtering for new feed types (Dark/Lit/All)
  const filteredByFeedType = React.useMemo(() => {
    if (isDarkFeed) {
      return baseTrades.filter((t) => t.exchangeId === FINRA_EXCHANGE_ID);
    } else if (isLitFeed) {
      return baseTrades.filter((t) => t.exchangeId !== FINRA_EXCHANGE_ID);
    }
    // For All - return all trades
    return baseTrades;
  }, [baseTrades, isDarkFeed, isLitFeed]);

  const apiTrades: Array<DarkPoolTrade> = filteredByFeedType;
  const trades: Array<DarkPoolTrade> = injectedPageData ? injectedTrades : apiTrades;
  const displayDateRange = React.useMemo(
    () =>
      dateFilterValue ? getDateRangeForDateFilter(dateFilterValue, trades, dateRange) : dateRange,
    [dateFilterValue, trades, dateRange],
  );

  // === Count-based polling (active when USE_TIMESTAMP_POLLING is false) ===
  React.useEffect(() => {
    if (USE_TIMESTAMP_POLLING) return;

    let interval: ReturnType<typeof setInterval> | undefined;

    if (!injectedPageData && activeTicker && hasAccess && shouldUseLatestData) {
      if (refetchCount) {
        refetchCount({ ticker: activeTicker.toUpperCase() });
      } else {
        getCount({ variables: { ticker: activeTicker.toUpperCase() } });
      }

      interval = setInterval(() => {
        if (refetchCount) {
          refetchCount({ ticker: activeTicker.toUpperCase() });
        } else {
          getCount({ variables: { ticker: activeTicker.toUpperCase() } });
        }
      }, POLL_INTERVAL);
    }

    return () => clearInterval(interval);
  }, [activeTicker, hasAccess, getCount, refetchCount, injectedPageData, shouldUseLatestData]);

  // Refetch trades when count / ticker / date range changes.
  React.useEffect(() => {
    if (USE_TIMESTAMP_POLLING) return;
    if (injectedPageData || !activeTicker || !hasAccess) return;
    if (!shouldUseLatestData && !dateRange) return;

    const ticker = activeTicker.toUpperCase();
    if (shouldUseLatestData) {
      fullRefetch
        ? fullRefetch({ ticker, sinceDate })
        : fullFeedQuery({ variables: { ticker, sinceDate } });
    } else if (dateRange) {
      const { startDate, endDate } = dateRange;
      if (fullHistoricalRefetch) {
        fullHistoricalRefetch({ ticker, startDate, endDate });
      } else {
        fullHistoricalQuery({ variables: { ticker, startDate, endDate } });
      }
    }
  }, [
    activeTicker,
    hasAccess,
    fullFeedQuery,
    fullHistoricalQuery,
    fullRefetch,
    fullHistoricalRefetch,
    count,
    injectedPageData,
    shouldUseLatestData,
    dateRange,
    sinceDate,
  ]);

  // === Timestamp-based polling (active when USE_TIMESTAMP_POLLING is true) ===
  React.useEffect(() => {
    if (!USE_TIMESTAMP_POLLING) return;
    if (!activeTicker || !hasAccess) return;
    if (!shouldUseLatestData) return;

    startPolling(POLL_INTERVAL);

    return () => stopPolling();
  }, [activeTicker, hasAccess, shouldUseLatestData, startPolling, stopPolling]);

  // When new data is detected via timestamp polling, refetch trades + count
  React.useEffect(() => {
    if (!USE_TIMESTAMP_POLLING) return;
    if (!activeTicker || !hasAccess) return;
    if (!shouldUseLatestData) return;

    if (latestData?.full_feed_equity_print_flow?.[0]?.createdAt) {
      const newTimestamp = latestData.full_feed_equity_print_flow[0].createdAt;
      if (newTimestamp !== lastSeenTimestamp) {
        setLastSeenTimestamp(newTimestamp);
        const ticker = activeTicker.toUpperCase();
        fullRefetch
          ? fullRefetch({ ticker, sinceDate })
          : fullFeedQuery({ variables: { ticker, sinceDate } });
        refetchCount?.({ ticker });
      }
    }
  }, [
    activeTicker,
    hasAccess,
    latestData,
    lastSeenTimestamp,
    fullFeedQuery,
    fullRefetch,
    refetchCount,
    shouldUseLatestData,
    sinceDate,
  ]);

  // Recent presets/custom ranges use the rolling latest rows and narrow client-side.
  // Older custom ranges use the historical query, which already applied bounds.
  const filteredTrades = React.useMemo(() => {
    const narrowed = filterTradesForDateSelection(trades, {
      isLatestPreset,
      useLatestData: shouldUseLatestData,
      dateRange: displayDateRange,
    });
    if (!filterState) return narrowed;
    const darkPoolFilterFn = getDarkPoolFilterFunction(filterState);
    return narrowed.filter(darkPoolFilterFn);
  }, [trades, filterState, isLatestPreset, shouldUseLatestData, displayDateRange]);

  // const filteredTrades = demoDarkpoolData.data.full_feed_equity_print_flow.filter(
  //   (t) => t.date === '2022-07-15',
  // );

  const darkPoolByTicker = React.useMemo(() => {
    const darkpoolTrades = filteredTrades;
    const groupedTrades: DarkPoolAggregatedDetails = {
      ticker: trades[0]?.ticker || '',
      trades: [],
      premium: 0,
      volume: 0,
      lateBuyCount: 0,
      lateSellCount: 0,
      goldenTradeCount: 0,
    };

    const open = filteredTrades[0]?.open || 0;

    darkpoolTrades.forEach((t) => {
      groupedTrades.trades.push(t);
      const signaturePrintStatus = getSignaturePrintStatus(t, { open });
      groupedTrades.premium += t.amount || 0;
      groupedTrades.volume += t.size || 0;

      if (signaturePrintStatus === SignaturePrintStatus.Buy) {
        groupedTrades.lateBuyCount += 1;
      } else if (signaturePrintStatus === SignaturePrintStatus.Sell) {
        groupedTrades.lateSellCount += 1;
      }

      if (getIsGoldenDarkPool(t.size, t.avgVolume)) {
        groupedTrades.goldenTradeCount += 1;
      }
    });

    return groupedTrades;
  }, [filteredTrades]);

  React.useEffect(() => {
    if (!hasAccess && !loading && !injectedPageData && activeTicker) {
      const utilTimeFilter = (() => {
        const timeFilterKey = TIME_FILTERS[activeTimeFilter]?.key;
        switch (timeFilterKey) {
          case TimeFilters.Today:
            return UtilTimeFilters.Today;
          case TimeFilters.Week:
            return UtilTimeFilters.Week;
          case TimeFilters.TwoWeeks:
            return UtilTimeFilters.TwoWeeks;
          case TimeFilters.Month:
            return UtilTimeFilters.Month;
          default:
            return UtilTimeFilters.Today;
        }
      })();

      const feedTypeParam = isDarkFeed ? 'dark' : isLitFeed ? 'lit' : 'all';
      const endpoint = `v1/products/dark-pool/${activeTicker}?feedType=${feedTypeParam}&timeFilter=${utilTimeFilter}`;

      fetchDarkPoolSummary({ endpoint });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, loading, injectedPageData, activeTicker, activeTimeFilter, feedType]);

  const overviewData =
    !hasAccess && apiSummary
      ? {
          ticker: apiSummary.ticker,
          trades: [],
          premium: apiSummary.premium,
          volume: apiSummary.volume,
          lateBuyCount: apiSummary.lateBuyCount,
          lateSellCount: apiSummary.lateSellCount,
          goldenTradeCount: apiSummary.goldenTradeCount,
        }
      : darkPoolByTicker;

  const buttonGroup = (
    <div className="flex flex-shrink-0 items-center">
      {feedTypeButtons.map((button) => (
        <DarkFeedTypeToggle
          key={button.type}
          isActive={feedType === button.type}
          onClick={() => setFeedType(button.type)}
        >
          {button.label}
        </DarkFeedTypeToggle>
      ))}
    </div>
  );

  return (
    <AutoTopGrid className="grid h-full min-w-0 max-w-full grid-cols-1 gap-2">
      <DarkPoolOverviewTable darkPoolByTicker={overviewData} activeTicker={activeTicker} />
      <DarkPoolTable
        includeDate
        permissionTitle="Unlock Dark + Lit Pool"
        permissionDescription="Institutions hide billions in dark pools. Discover the trades and levels institutions don't want you to see."
        trades={filteredTrades}
        hasAccess={hasAccess || !!injectedPageData}
        viewer={viewer}
        searchValue={''}
        loading={hasAccess ? loading && trades.length === 0 : false}
        error={hasAccess ? error : undefined}
        watchListTickers={[]}
        isWatchListOnly={false}
        hasWatchListItems={false}
        hideHeader={hideHeader}
        hideLevelsColumn={hideLevelsColumn}
        tickerDetailsMap={tickerDetailsMap}
        right={buttonGroup}
      />
    </AutoTopGrid>
  );
};

export default TickerDarkPoolTable;
